/** Billing routes — cost dashboard + invoice generation. */

import type { ApiClient } from "../client";

export interface DailyRollup {
  rollup_date: string;
  aws_attributed: string | null;
  usage_cost: string | null;
  breakdown: Record<string, unknown> | null;
}

export interface TenantCostSummary {
  tenant_id: string;
  display_name: string;
  total_aws: string;
  total_usage: string;
  total: string;
}

export interface PlatformCostResponse {
  since: string;
  until: string;
  platform_total_aws: string;
  platform_total_usage: string;
  platform_total: string;
  by_tenant: TenantCostSummary[];
}

export interface TenantCostResponse {
  tenant_id: string;
  since: string;
  until: string;
  daily: DailyRollup[];
}

export interface EventTypeTrend {
  event_type: string;
  day: string;
  total_quantity: string;
}

export interface TenantEventTrendsResponse {
  tenant_id: string;
  since: string;
  until: string;
  trends: EventTypeTrend[];
}

export type PlanType = "PER_AGENT" | "PER_USAGE" | "FLAT_RATE" | "WHOLESALE";

/** A named platform plan (V065) — the picker's options + seat limits. */
export interface BillingPlanCatalogEntry {
  id: string;
  name: string;
  plan_type: PlanType;
  base_fee: string;
  agent_fee: string;
  markup_pct: string;
  flat_rate: string;
  discount_pct: string;
  monthly_minimum: string;
  max_agents: number | null;
  max_admins: number | null;
  active: boolean;
  display_order: number;
}

export interface BillingPlanBody {
  plan_type: PlanType;
  base_fee?: string;
  agent_fee?: string;
  markup_pct?: string;
  flat_rate?: string;
  discount_pct?: string;
  monthly_minimum?: string;
  /** Which named catalog plan this came from (supplies fallback seat limits). */
  catalog_plan_id?: string | null;
  /** Per-tenant seat overrides; null = inherit the catalog plan's limit. */
  max_agents?: number | null;
  max_admins?: number | null;
  stripe_customer_id?: string | null;
  active?: boolean;
}

export interface BillingPlanOut extends BillingPlanBody {
  id: string;
  tenant_id: string;
  updated_at: string;
}

/** Seat usage vs plan limit (null limit = unlimited). */
export interface SeatUsage {
  used: number;
  limit: number | null;
}

export interface StaffSeats {
  agents: SeatUsage;
  admins: SeatUsage;
}

export interface InvoiceGenerateRequest {
  period?: string;
  tenant_id?: string | null;
  dry_run?: boolean;
}

export interface InvoiceLineItem {
  description: string;
  amount_usd: number;
  amount_cents: number;
}

export interface InvoiceResult {
  tenant_id: string;
  plan_type: string;
  amount_usd: number;
  stripe_invoice_id: string | null;
  line_items: InvoiceLineItem[];
  /** True when a prior run already invoiced this tenant for the period (skipped). */
  already_billed: boolean;
}

export interface InvoiceGenerateResponse {
  period_start: string;
  period_end: string;
  dry_run: boolean;
  invoices: InvoiceResult[];
}

export type Granularity = "daily" | "weekly" | "monthly";

export interface TimeseriesPoint {
  period_start: string;
  total_aws: string;
  total_usage: string;
  total: string;
}

export interface CostTimeseriesResponse {
  since: string;
  until: string;
  granularity: Granularity;
  points: TimeseriesPoint[];
}

export interface MarginRow {
  tenant_id: string;
  display_name: string;
  plan_type: string | null;
  revenue: string;
  aws_cost: string;
  usage_cost: string;
  /** This tenant's own subscriptions plus its pro-rata share of the
   *  platform-wide ones, split by usage share over the same window (V080). */
  subscription_cost: string;
  margin: string;
  margin_pct: string | null;
  at_risk: boolean;
}

export interface MarginResponse {
  since: string;
  until: string;
  risk_threshold: string;
  platform_revenue: string;
  platform_aws_cost: string;
  platform_subscription_cost: string;
  platform_margin: string;
  rows: MarginRow[];
}

export type BillingCycle = "MONTHLY" | "ANNUAL" | "ONE_OFF";

/** A recurring third-party bill that makes up cost-to-serve alongside AWS. */
export interface Subscription {
  id: string;
  /** null = platform-wide, allocated across tenants pro-rata by usage. */
  tenant_id: string | null;
  tenant_name: string | null;
  vendor: string;
  name: string;
  category: string | null;
  amount: string;
  currency: string;
  billing_cycle: BillingCycle;
  active_from: string;
  active_to: string | null;
  notes: string | null;
  /** `amount` normalised to a month; ONE_OFF contributes 0. Server-derived. */
  monthly_equivalent: string;
}

export interface SubscriptionCreate {
  tenant_id?: string | null;
  vendor: string;
  name: string;
  category?: string | null;
  amount: string | number;
  currency?: string;
  billing_cycle?: BillingCycle;
  active_from?: string;
  active_to?: string | null;
  notes?: string | null;
}

export interface SubscriptionUpdate extends Partial<SubscriptionCreate> {
  /** A null tenant_id in a PATCH is ambiguous — this says "make it
   *  platform-wide" explicitly. */
  clear_tenant?: boolean;
  clear_active_to?: boolean;
}

export interface AgentCostRow {
  user_id: string | null;
  full_name: string | null;
  email: string | null;
  usage_cost: string;
  total_events: number;
  breakdown: Record<string, unknown> | null;
}

export interface AgentCostResponse {
  tenant_id: string;
  since: string;
  until: string;
  total_usage: string;
  rows: AgentCostRow[];
}

export const billingService = {
  /** Platform cost bucketed daily/weekly/monthly (the D/W/M tracker). */
  getCostTimeseries(
    client: ApiClient,
    params?: { granularity?: Granularity; since?: string; until?: string },
  ) {
    return client.request<CostTimeseriesResponse>("/admin/billing/timeseries", {
      query: params,
    });
  },

  /** Per-tenant revenue vs. AWS cost + margin (super_admin loss guard). */
  getMargin(client: ApiClient, params?: { since?: string; until?: string }) {
    return client.request<MarginResponse>("/admin/billing/margin", {
      query: params,
    });
  },

  /** Per-agent retail usage (super_admin any tenant; brokerage_admin own). */
  /** Recurring third-party bills (super_admin). */
  listSubscriptions(client: ApiClient, params?: { include_ended?: boolean }) {
    return client.request<Subscription[]>("/admin/billing/subscriptions", {
      query: params,
    });
  },

  createSubscription(client: ApiClient, body: SubscriptionCreate) {
    return client.request<Subscription>("/admin/billing/subscriptions", {
      method: "POST",
      body,
    });
  },

  updateSubscription(client: ApiClient, id: string, body: SubscriptionUpdate) {
    return client.request<Subscription>(
      `/admin/billing/subscriptions/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    );
  },

  /** Prefer setting active_to — deleting removes the cost from periods it was
   *  genuinely billed in, changing margins already reported. */
  deleteSubscription(client: ApiClient, id: string) {
    return client.request<void>(
      `/admin/billing/subscriptions/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },

  getAgentCosts(
    client: ApiClient,
    params?: { tenant_id?: string; since?: string; until?: string },
  ) {
    return client.request<AgentCostResponse>("/admin/billing/agent-costs", {
      query: params,
    });
  },

  /** Named platform plans for the picker + upgrade nudge (staff-readable). */
  getPlanCatalog(client: ApiClient, params?: { include_inactive?: boolean }) {
    return client.request<BillingPlanCatalogEntry[]>("/admin/billing/catalog", {
      query: params,
    });
  },

  /** Seat usage vs plan limit for a tenant (own tenant for brokerage_admin). */
  getStaffSeats(client: ApiClient, params?: { tenant_id?: string }) {
    return client.request<StaffSeats>("/admin/staff/seats", { query: params });
  },

  getPlatformCosts(
    client: ApiClient,
    params?: { since?: string; until?: string },
  ) {
    return client.request<PlatformCostResponse>("/admin/billing/costs", {
      query: params,
    });
  },

  getTenantCosts(
    client: ApiClient,
    tenantId: string,
    params?: { since?: string; until?: string },
  ) {
    return client.request<TenantCostResponse>(
      `/admin/billing/costs/${tenantId}`,
      {
        query: params,
      },
    );
  },

  getTenantEventTrends(
    client: ApiClient,
    tenantId: string,
    params?: { since?: string; until?: string },
  ) {
    return client.request<TenantEventTrendsResponse>(
      `/admin/billing/costs/${tenantId}/events`,
      { query: params },
    );
  },

  putBillingPlan(client: ApiClient, tenantId: string, body: BillingPlanBody) {
    return client.request<BillingPlanOut>(
      `/admin/tenants/${tenantId}/billing-plan`,
      {
        method: "PUT",
        body,
      },
    );
  },

  generateInvoices(client: ApiClient, body: InvoiceGenerateRequest) {
    return client.request<InvoiceGenerateResponse>(
      "/billing/invoices/generate",
      {
        method: "POST",
        body,
      },
    );
  },
};
